import { ConfigServices, Services } from '../services';
import {
    action,
    computed,
    observable,
    reaction,
    configure,
    toJS,
    isArrayLike,
} from 'mobx';
import { theme } from '../Components/theme';
import { mergeAppStylesWithTheme } from '../utils/mergeAppStylesWithTheme';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import snakeCase from 'lodash/snakeCase';
import { getParams, getFilterParams, Segmentation } from '../utils';
import getSortableColumn from '../utils/getSortableColumn';
import isExistingAdvancedSort from '../utils/isExistingAdvancedSort';
import getColumnLabel from '../utils/getColumnLabel';
import * as FileSaver from 'file-saver';
import { getExtension } from 'mime';
import {
    EXPANDED_PARAM,
    EXPAND_ALL_PARAM,
    DISALLOWED_FILTER_PARAMS,
    SORT_DIRECTIONS,
    ADVANCED_SORT_TYPES,
    PAGE_START,
    PAGE_LIMIT,
    INPUT_EXPAND_ALL_PARAM,
    INPUT_EXPANDED_PARAM,
    INPUT_PAGE_START,
    INPUT_PAGE_LIMIT,
} from '../constants';
import { Params } from '../models/params';
import { Stores } from '../models/Stores';
import { AppParams } from '../models/configuration/AppParams';
import { LoadingTarget } from '../models/loading';
import { CsvHelper } from '../utils/CsvHelper';
import { translate } from '../Components/Translate';
import formatter, { padStart } from '../utils/formatter';
import { ExplorerDownloadDisclaimerSection } from '../models/configuration/explorer/ExplorerDownloadDisclaimerSection';
import { ExplorerWatchlist } from '../models/configuration/explorer/ExplorerWatchlist';
import { FilterSearchKeys } from '../models/components/Filter';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { LinkFilter } from '../models/configuration/LinkFilter';
import { SegmentationFilter } from '../models/configuration/SegmentationFilter';
import { FundDetailLists } from '../models/configuration/FundDetailLists';
import { ExpandStatus } from '../models/expandStatus';
import { BreakpointSize } from '../types';
import { HistoricDataRequestConfig } from '../models/configuration/HistoricDataRequestConfig';
import { Filter } from '../models/configuration/filter/Filter';
import {
    isNullOrEmpty,
    isNullOrUndefined,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import fileNameSanitizer from '@kurtosys/ksys-app-template/dist/utils/fileNameSanitizer';
import {
    ISearchEntityRequest,
    IEntityEntity,
    IEntity,
} from '@kurtosys/ksys-app-template/dist/models/api/search';
import { models } from '@kurtosys/ksys-app-template';
import { GetDataOptions } from '../models/services/GetDataOptions';
import { LocalizationFormat } from '../models/configuration/LocalizationFormat';
import { Aliases } from '../models/configuration/Aliases';
import { GlobalValueAliases } from '../models/configuration/global/ValueAliases';
import { DataTextMapping } from '../models/configuration/DataTextMapping';
import {
    AssetStore,
    QueryStore,
    FilterStore,
    BaseStore,
    DocumentStore,
    DisclaimerStore,
    ConfigStore,
    ColumnStore,
    DocumentModuleStore,
} from '.';
import { SuperscriptScope } from './DisclaimerStore';
import { GlobalDisclaimerStore } from './GlobalDisclaimerStore';
import { DataLocator } from '../models/configuration/DataLocator';
import { EntityType } from '../models/configuration/EntityType';
import { GroupedFunds, TWatchlistHierarchy } from '../models/groupedFunds';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { AppProps } from '../models/AppProps';
import { ExplorerDownloadType } from '../models/configuration/explorer/ExplorerDownloadType';
import { ExplorerDownload } from '../models/configuration/explorer/ExplorerDownload';
import { utils } from '@kurtosys/ksys-app-template/dist';
import { ExplorerDownloadPdf } from '../models/configuration/explorer/ExplorerDownloadPdf';
import {
    UiInputQuery,
    UiInputQueryType,
} from '../models/configuration/explorer/UiInputQuery';
import {
    IQueryOptions,
    IQueryContext,
} from '@kurtosys/ksys-app-template/dist/models/query';
import { ShareClass, ShareClassDocument } from '../models/entity';
import {
    DocumentSearch,
    EntitySearch,
} from '../models/configuration/documents/DocumentSearch';
import getQueryResults from '../utils/query/getQueryResults';
import { AnalyticsHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import {
    IFundCenterContext,
    IAnalytics,
} from '@kurtosys/ksys-app-template/dist/models/helpers/AnalyticsHelper';
import { languageFromCulture } from '../utils/languageFromCulture';
import { ModalHelper } from '../utils/accessibility/ModalHelper';
import { BaseDisclaimer } from '../models/configuration/disclaimers/BaseDisclaimer';
import {
    IEntityData,
    IJoinMap,
} from '@kurtosys/ksys-app-template/dist/models/api/document';
import { transformValue } from '../utils/transform/transform';
import { ITransformOptions } from '@kurtosys/ksys-app-template/dist/models/query/transforms';
import { ErrorTracking } from '../utils/errorHandling/ErrorTracking';
import {
    DownloadControlActions,
    PreviewActionOptions,
} from '../models/configuration/explorer/DownloadControls';
import { DocumentSearchRequests } from '../models/config';
import { developmentAppConfig } from '../development.appConfig';
import { registerAppLoaded, registerAppStart } from '../utils/initialize';
import { Manifest } from '../configuration/Manifest';
import { PaginationStore } from './PaginationStore';

// If a parent website is using mobx, a warning is logged that multiple mobx instances are active, which "might lead to unexpected results".
// Calling isolateGlobalState signals mobx that it is the intention that multiple mobx instances are active,
// without tracking each other, and will suppress the warning.
// See https://github.com/mobxjs/mobx/issues/1082
configure({ isolateGlobalState: true });

export class DataStore extends BaseStore {
    public filterStore: FilterStore | undefined;
    public assetStore: AssetStore | undefined;
    public analyticsHelper: AnalyticsHelper<IFundCenterContext>;
    public root: HTMLElement;
    public shadowRoot: ShadowRoot;
    public paginationStore: PaginationStore | undefined;

    @computed
    get fundTypeSearch() {
        if (this.config.fundTypeProperty) {
            return [
                {
                    property: this.config.fundTypeProperty,
                    values: [this.config.selectedFundType.fundType],
                    matchtype: 'MATCH',
                },
            ];
        }

        return [];
    }

    @computed
    get switchToCardView() {
        const configOption =
            this.config &&
            this.config.responsiveConfig &&
            this.config.responsiveConfig.switchToCardView;
        return configOption || 'sm';
    }

    @computed
    get processedFunds() {
        const processedFunds = this.funds.map(fund => {
            const { filteredDocuments } = new DocumentStore(
                this.stores,
                this.config,
                fund,
                this.globalDocuments
            );

            return {
                ...fund,
                documents: filteredDocuments,
            };
        });

        return processedFunds;
    }

    @computed
    get hasSubFunds() {
        const firstFund = this.groupedFunds[0];
        return (
            firstFund && Object.keys(firstFund).indexOf('subGroupedFunds') > -1
        );
    }

    @computed
    get groupedFunds(): Array<GroupedFunds> {
        const groupByFunds = groupBy(
            this.processedFunds,
            `properties_pub.${this.config.fundGroupProperty}.value`
        );
        const groupedFunds = Object.keys(groupByFunds).map(
            (fundGroupName, index) => ({
                fundName: fundGroupName,
                grouped: !!this.config.fundGroupProperty,
                shareClasses: groupByFunds[fundGroupName],
                columns: this.columnStore.columns,
                consecutiveIndex: index,
            })
        );
        const groupedSubFundsProperty = this.config.fundSubGroupProperty;
        if (
            !isNullOrEmpty(groupedFunds) &&
            this.config.fundGroupProperty &&
            groupedSubFundsProperty
        ) {
            let consecutiveIndex = 0;
            const nonGroupHeaderText = this.config.fundGroupHeader
                .nonGroupHeaderText;
            const groupedWithSubFunds: Array<GroupedFunds> = groupedFunds.map(
                (groupedFund, index) => {
                    const subGroupByFunds = groupBy(
                        groupedFund.shareClasses,
                        `properties_pub.${groupedSubFundsProperty}.value`
                    );
                    groupedFund.consecutiveIndex =
                        index === 0 ? index : (consecutiveIndex += 1);
                    const subGroupedFunds = Object.keys(subGroupByFunds).map(
                        fundSubGroupName => {
                            const valid = fundSubGroupName !== 'undefined';
                            return {
                                fundSubName: !valid
                                    ? nonGroupHeaderText || 'Other'
                                    : fundSubGroupName,
                                shareClasses: subGroupByFunds[fundSubGroupName],
                                valid,
                                parent: groupedFund.fundName,
                                consecutiveIndex: consecutiveIndex += 1,
                            };
                        }
                    );

                    return {
                        subGroupedFunds,
                        ...groupedFund,
                    } as GroupedFunds;
                }
            );
            return observable(groupedWithSubFunds);
        }
        return observable(groupedFunds);
    }

    @computed
    get culture() {
        return this.defaultCulture || this.config.defaultCulture;
    }

    // Watchlist

    @computed
    get watchListKey() {
        let key = 'ksys_watchlist';
        if (this.config.applicationName) {
            key += `_${this.config.applicationName}`;
        }
        if (
            this.config.selectedFundType &&
            !this.config.selectedFundType.watchlist
        ) {
            const fundTypeLabel = this.config.selectedFundType.label;
            key += `_${fundTypeLabel}`;
        } else if (
            this.config.selectedFundType &&
            this.config.selectedFundType.watchlist &&
            this.config.selectedFundType.watchlist.scope === 'unique'
        ) {
            const fundTypeLabel =
                this.config.selectedFundType.watchlist.key ||
                this.config.selectedFundType.label;
            key += `_${fundTypeLabel}`;
            if (
                this.config.selectedModule &&
                this.config.selectedModule.config &&
                this.config.selectedModule.config.explorer &&
                this.config.selectedModule.config.explorer.watchlist &&
                this.config.selectedModule.config.explorer.watchlist.scope ===
                    'unique'
            ) {
                const moduleLabel =
                    this.config.selectedModule.config.explorer.watchlist.key ||
                    this.config.selectedModule.label;
                key += `_${moduleLabel}`;
            }
        }
        return key;
    }

    @computed
    get watchListEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.config.watchListEnabled;
        const property =
            (this.watchListConfig && this.watchListConfig.property) ||
            this.config.watchListProperty;

        return !!(enabled && property);
    }

    @computed
    get watchListSearchEnabled() {
        const enabled =
            (this.watchListConfig && this.watchListConfig.enabled) ||
            this.config.watchListEnabled;
        return !!(enabled && this.watchListActive);
    }

    @computed
    get watchListCount() {
        return this.watchList.length;
    }

    @computed
    get watchListSearch() {
        const { watchListProperty } = this.config;
        const watchList = this.watchList;
        return [
            {
                property: watchListProperty,
                values: watchList,
                matchtype: 'MATCH',
            },
        ];
    }

    @computed
    get watchListConfig(): ExplorerWatchlist {
        return (
            this.config &&
            this.config.explorer &&
            this.config.explorer.watchlist
        );
    }

    // Identifier search config used in entity searches
    // This is derived from merging config.identifierSearch with a search term.
    // We use the property used for grouping funds as the default property to search on. Note that this may be overridden by identifierSearch config.
    @computed
    get identifierSearch() {
        return {
            properties: [this.config.fundGroupProperty || ''],
            fuzzy: false,
            ...this.config.identifierSearch,
            ...{ value: this.identifierSearchValue },
        };
    }

    @computed
    get searchValues() {
        return {
            properties: this.identifierSearchProperties,
            placeholder: this.identifierSearchPlaceholder,
            value: this.identifierSearchValue,
        };
    }

    @computed
    get identifierSearchProperties() {
        const aliases = this.identifierSearchAliases;
        const { identifierSearch } = this.config;
        const { translation } = this.stores;

        if (!identifierSearch || !identifierSearch.properties) {
            return [];
        }

        const properties = identifierSearch.properties.map(code => {
            let name = aliases[code] || this.getClassPropertyLabel(code);
            name = translate(translation, name);
            return name;
        });
        return properties;
    }

    @computed
    get identifierSearchAliases() {
        const placeholder = this.config.identifierSearch.placeholder;
        if (placeholder && placeholder.aliases) {
            const aliases = {};
            for (const alias of placeholder.aliases) {
                aliases[alias.key] = alias.value;
            }
            return aliases;
        }
        return {};
    }

    @computed
    get identifierSearchPlaceholder() {
        const { placeholder } = this.config.identifierSearch;
        if (placeholder) {
            const { badge } = placeholder;
            return {
                format: placeholder.format || {
                    singular: 'Search {total} share class',
                    plural: 'Search {total} share classes',
                },
                badge: badge && {
                    show: badge.show && !!badge.format,
                    format: badge.format,
                    align: badge.align || 'right',
                },
            };
        } else {
            return {
                format: {
                    singular: 'Search {total} share class',
                    plural: 'Search {total} share classes',
                },
            };
        }
    }

    // Documents join spec used in entity searches
    // Returns config.documentsJoinSpec combined with documentSearch and documentSearchBySegmentation to limit the documents displayed.
    @computed
    get documentsJoinSpec() {
        const joinSpecSearch = this.config.documentSearch
            .concat(this.config.documentSearchBySegmentation.slice())
            .reduce((accumulator, currentValue) => {
                // Replace placeholders for joinSpec search values
                const parsedSearchValues = currentValue.values.map(
                    searchValue =>
                        replacePlaceholders({
                            text: searchValue,
                            appParams: this.appDataProps,
                        })
                );
                currentValue.values = parsedSearchValues;

                const existingPropertySearch = accumulator.find(
                    a => a.property === currentValue.property
                );

                if (existingPropertySearch) {
                    const existingValues = existingPropertySearch.values.slice();
                    const currentValues = currentValue.values.reduce(
                        (acc, value) => {
                            if (existingValues.indexOf(value) < 0) {
                                acc.push(value);
                            }
                            return acc;
                        },
                        []
                    );
                    existingPropertySearch.values = [
                        ...existingValues,
                        ...currentValues,
                    ];
                    return accumulator;
                }

                accumulator.push(currentValue);
                return accumulator;
            }, []);

        return this.config.documentsJoinSpec.map(joinSpec => {
            joinSpec.search = joinSpecSearch;
            return joinSpec;
        });
    }

    // Get filter properties that apply to entity search from filters config and query string filter params
    @computed
    get filterProperties() {
        return this.config.filtersConfig
            .filter(filter => filter.type === this.config.entityType)
            .map(filter => filter.property)
            .concat(Object.keys(this.filterParams))
            .filter(property => !DISALLOWED_FILTER_PARAMS.includes(property));
    }

    // Build a search for fund/getFilters based on allowedValues specified in filter config
    @computed
    get filterPropertiesAllowedValuesSearch() {
        return this.config.filtersConfig
            .filter(filter => filter.type === this.config.entityType)
            .map(filter => {
                if (filter.allowedValues && filter.allowedValues.length > 0) {
                    return {
                        property: filter.property,
                        values: filter.allowedValues || [],
                        matchtype: 'MATCH',
                    };
                }
            })
            .filter(filter => filter !== undefined);
    }

    // Get filter properties that apply to document meta from filters config
    @computed
    get documentFilterCodes() {
        return this.config.filtersConfig
            .filter(filter => filter.type === 'DOCUMENT_META')
            .map(filter => filter.property);
    }

    // Get filter properties that apply to document inferred from filters config
    @computed
    get documentFilterInferredCodes() {
        return this.config.filtersConfig
            .filter(filter => filter.type === 'DOCUMENT_INFERRED')
            .map(filter => filter.property);
    }

    // Build a search for documents/getFilters based on allowedValues specified in filter config
    @computed
    get documentFilterAllowedValuesSearch() {
        const documentSearchBySegmentation =
            toJS(this.config.documentSearchBySegmentation) || [];
        const searchValues = this.config.filtersConfig
            .filter(
                filter =>
                    filter.type === 'DOCUMENT_META' ||
                    filter.type === 'DOCUMENT_INFERRED'
            )
            .map(filter => {
                if (filter.allowedValues && filter.allowedValues.length > 0) {
                    const filterProperty = filter.property;
                    const segmentationSearch = documentSearchBySegmentation.find(
                        search => search.property === filterProperty
                    );
                    if (!segmentationSearch) {
                        return {
                            property: filterProperty,
                            values:
                                (toJS(filter.allowedValues) as Array<string>) ||
                                [],
                            matchtype: 'MATCH' as 'MATCH' | 'LIKE',
                        };
                    }
                }
            })
            .filter(filter => filter !== undefined);
        for (const searchValue of searchValues) {
            documentSearchBySegmentation.push(searchValue);
        }
        return documentSearchBySegmentation;
    }

    // Get filters that apply to document properties from filters config
    @computed
    get documentPropertyFilters() {
        return this.config.filtersConfig.filter(
            filter => filter.type === 'DOCUMENT_PROPERTY'
        );
    }

    // Get sort matching fundGroupProperty
    @computed
    get fundGroupPropertySort() {
        return this.config.sort.find(
            s => s.key === this.config.fundGroupProperty
        );
    }

    @computed
    get hasAllFunds() {
        return this.funds.length >= this.total;
    }

    @computed
    get hideLoadMoreButton() {
        const pagingConfig = this.config.paging;
        if (pagingConfig && pagingConfig.mode === 'paginationApp') {
            return true;
        } else {
            if (this.hasAllFunds) {
                if (
                    pagingConfig &&
                    pagingConfig.hideLoadMoreButtonWhenAllRecordsRetrieved
                ) {
                    return true;
                }
            }
            return false;
        }
    }

    @computed
    get disclaimerStore(): DisclaimerStore {
        return this.stores && this.stores.disclaimer;
    }

    // Get disclaimers for header and footer as specified in config
    // Since the intended use is to display general purpose disclaimers not specifically for a fund,
    // we assume that disclaimers for the given types are not linked to an entity or segmentation list and do not have multiple disclaimers.
    @computed
    get headerDisclaimers() {
        const baseDisclaimers: Array<BaseDisclaimer> = this.config.headerDisclaimerTypes.map(
            disclaimerType => {
                return {
                    disclaimerType,
                } as BaseDisclaimer;
            }
        );
        return this.disclaimerStore.getDisclaimersForDisplay(baseDisclaimers);
    }

    @computed
    get footerDisclaimers() {
        const baseDisclaimers: Array<BaseDisclaimer> = this.config.footerDisclaimerTypes.map(
            disclaimerType => {
                return {
                    disclaimerType,
                } as BaseDisclaimer;
            }
        );
        return this.disclaimerStore.getDisclaimersForDisplay(
            baseDisclaimers,
            true,
            SuperscriptScope.module
        );
    }

    @computed
    get fundListFilters(): Array<any> {
        const { fundListFilters } = this.config.selectedModule.config;
        if (fundListFilters) {
            return fundListFilters.map(config => {
                const { key, segmentationFilter } = config;
                return {
                    key,
                    fundList: this.getFundListsFromSegmentationFilter(
                        segmentationFilter
                    ),
                    fundListExclusion: this.getFundListExclusionsFromSegmentationFilter(
                        segmentationFilter
                    ),
                };
            });
        }
        return [];
    }

    @computed
    get fundLists(): string | Array<string> {
        const segmentationFilter = this.config.selectedModule.config
            .segmentationFilter;
        return this.getFundListsFromSegmentationFilter(segmentationFilter);
    }

    @computed
    get fundListExclusions(): Array<string> {
        const segmentationFilter = this.config.selectedModule.config
            .segmentationFilter;
        return this.getFundListExclusionsFromSegmentationFilter(
            segmentationFilter
        );
    }

    @computed
    get defaultShowChange() {
        let showChange = {
            showIcon: true,
            changeUpColor: 'inherit',
            changeDownColor: 'inherit',
        };

        if (
            this.config.selectedModule &&
            this.config.selectedModule.config &&
            this.config.selectedModule.config.explorer &&
            this.config.selectedModule.config.explorer.showChange
        ) {
            showChange = this.config.selectedModule.config.explorer.showChange;
        }

        return showChange;
    }

    configServices: ConfigServices;
    services: Services;
    apiUrl;
    baseUrl;
    @observable servicesEntityType?: EntityType;

    // App config
    @observable token = ''; // API token
    theme: any = {}; // App theme
    @observable config = {} as ConfigStore; // App config from config file
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection
    columnGroups = {}; // ShareClass column groups to be set via querystring
    @observable columnStore = {} as ColumnStore; // Column processing and related functions

    // App loading states
    @observable isBootstrapped = false;
    @observable isExpanded = false;
    @observable isExpandAll = false;
    @observable pageStart = null;
    @observable pageLimit = null;

    // culture
    @observable defaultCulture = undefined; // this.config.defaultCulture; // Culture to use
    @observable cultures = []; // System cultures

    // Disclaimers
    get rawDisclaimers() {
        return GlobalDisclaimerStore.rawDisclaimers;
    }
    get globalDisclaimersMap() {
        return GlobalDisclaimerStore.disclaimerMap;
    }
    @observable cellDisclaimers = [];

    // Search
    @observable fundlist = ''; // Segmentation list
    @observable identifierSearchValue = ''; // Search term
    @observable total = 0; // Search total
    @observable start = 0; // Search start
    @observable funds = []; // Search data
    @observable fundsForCsv = [];

    @observable.ref watchlistHierarchy: TWatchlistHierarchy | undefined;

    // Fund Detail Lists
    @observable fundDetailLists: FundDetailLists;

    // Filters
    @observable filters = {}; // Filter codes and values
    @observable activeFilters = {}; // Filter codes and values returned by getFilters with search
    @observable filterSearch = []; // Selected entity filters
    @observable filterParams = {}; // Filters from query string parameters
    @observable documentFilterSearch = []; // Selected document meta filters
    @observable documentFilterInferredSearch = []; // Selected document inferred filters
    @observable documentPropertySearch = []; // Selected document property filters
    @observable userFilters = {}; // The filter values that the user last had selected, pulled from the storage helper

    // Watchlist
    @observable watchList = [];
    @observable watchListActive = false;
    @observable watchListActiveSetByUser = false;

    // Sorting
    @observable propertySort = [];
    @observable advancedSort;

    // Map of column index to selected column group index
    selectedGroupMap = observable.map();

    // Class Properties
    @observable classPropertiesByCode = {};

    // Global documents
    @observable.ref globalDocuments = [];

    // Documents Entity Data, will contain the document entity mapping data
    @observable documentEntityData: {
        documentEntities?: Record<
            string,
            Pick<IEntity, 'type' | 'properties_pub'>
        >;
        documentJoinMap?: Record<string, Array<string>>;
    } = {
        documentEntities: {},
        documentJoinMap: {},
    };

    // Documents module
    @observable documentModule: DocumentModuleStore;

    @observable getFundsRunning: boolean = false;

    @observable getFundDetailListsRunning: boolean = false;

    @observable getHistoricDataRunning: boolean = false;

    @observable appDataProps: AppParams = {}; // data properties of root element

    @observable fundLinkFilter: Record<string, Array<string>> = {};

    debounceTimeout: NodeJS.Timeout;

    @observable expandStatus: ExpandStatus = {};

    constructor(stores: Stores, props) {
        super(stores);
        this.setupDefaultFundCenterContext = this.setupDefaultFundCenterContext.bind(
            this
        );

        if (props.root) {
            this.root = props.root;
        }

        if (props.shadowRoot) {
            this.shadowRoot = props.shadowRoot;
        }
        registerAppStart(this.appDataProps);
        // Call init function with props passed from the main app component
        // Props will be the attributes from the element where the app is mounted
        // this.init(props);

        // Search called in reaction to changes in search term
        // This is debounced to allow the user to finish typing
        reaction(
            () => this.identifierSearchValue,
            identifierSearchValue => {
                if (this.debounceTimeout !== undefined) {
                    clearTimeout(this.debounceTimeout);
                }
                this.debounceTimeout = setTimeout(() => {
                    if (
                        identifierSearchValue.length === 0 ||
                        identifierSearchValue.length >=
                            this.config.autoSearchMinChar
                    ) {
                        if (this.config.isDocumentModule) {
                            if (this.documentModule) {
                                this.documentModule.getDocuments();
                            }
                        } else {
                            this.getFunds();
                            this.getFundDetailLists();
                        }
                        this.analyticsHelper.logEvent({
                            event: 'search',
                            eventType: 'click',
                            context: {
                                searchTerm: this.identifierSearchValue,
                            },
                        });
                    }
                }, this.config.searchMillisecondTimeout);
            }
        );

        // Search and getActiveFilters in reaction to:
        // entity filter selections
        // watchList toggle
        reaction(
            () => [
                this.filterSearch,
                this.documentFilterSearch,
                this.documentFilterInferredSearch,
                this.watchListActive,
            ],
            async filters => {
                if (this.isBootstrapped) {
                    if (this.config.isDocumentModule) {
                        if (this.documentModule) {
                            this.documentModule.getDocuments();
                        }
                    } else {
                        await this.getWatchList();
                        this.getFunds();
                        this.getFundDetailLists();
                        this.getActiveFilters();
                        // If the minimum filters are met, this.getFunds() will fire a call to loadModuleDisclaimers()
                        // If the minimum filters are not met, the call to loadModuleDisclaimers() below will refresh the disclaimers
                        if (!this.hasMinimumFiltersNeeded()) {
                            this.disclaimerStore.loadModuleDisclaimers();
                        }
                    }
                }
            }
        );

        // Save watchList
        reaction(
            () => this.watchList,
            watchList => {
                this.stores.watchlist.setWatchlist(
                    this.watchListKey,
                    watchList
                );
            }
        );

        // Reaction to change in module/tab gets funds and filters for new config
        reaction(
            () => this.config.currentModuleId,
            async currentModuleId => {
                if (this.isBootstrapped) {
                    // explicit false so that default preserveExpandedRow is true
                    if (
                        this.config.selectedFundType.preserveExpandedRow ===
                        false
                    ) {
                        this.expandStatus = {};
                    }
                    if (this.config.isDocumentModule) {
                        this.documentModule = new DocumentModuleStore(this);
                        this.getDocumentFilters();
                        this.disclaimerStore.loadModuleDisclaimers();
                    } else {
                        this.stores.paginationStore.updatePaging(
                            0,
                            this.config.limit
                        );
                        this.clearWatchlistHierarchy();
                        await this.getWatchList();
                        this.setInitialSort();
                        this.getFunds();
                        this.getFundDetailLists();
                        this.getFilters();
                        this.getActiveFilters();
                        this.getDocumentFilters();
                        this.getGlobalDocuments();
                        this.getClassProperties();
                    }
                }
            }
        );

        // Reaction to change in fund type clears filters and gets filters for new config
        reaction(
            () => this.config.currentFundType,
            async currentFundType => {
                if (this.isBootstrapped) {
                    this.stores.paginationStore.updatePaging(
                        0,
                        this.config.limit
                    );
                    if (this.config.clearFiltersOnChange) {
                        this.clearFilters();
                    }

                    this.config.setDefaultModule();

                    this.updateServicesEntityType();

                    if (this.config.isDocumentModule) {
                        this.documentModule = new DocumentModuleStore(this);
                        this.getDocumentFilters();
                    } else {
                        this.clearWatchlistHierarchy();
                        this.expandStatus = {};
                        await this.getWatchList();
                        this.getFilters();
                        this.getDocumentFilters();
                        this.getGlobalDocuments();
                        this.getClassProperties();
                    }
                    this.getFunds();
                }
            }
        );

        // Update Pagination
        reaction(
            () => [
                this.stores.paginationStore.pageStart,
                this.stores.paginationStore.pageLimit,
            ],
            paging => {
                if (!this.config.isDocumentModule) {
                    this.getFunds();
                    this.getFundDetailLists();
                }
            }
        );
    }

    @action
    loadSearchFromEvent = (
        eventDetail: models.eventBus.IGlobalSearchChangeDetail
    ) => {
        const searchTerm = eventDetail.searchTerm;
        this.search(searchTerm);
    };

    @computed
    get breakpointSize(): BreakpointSize {
        return this.stores?.screenSize?.breakpointSize;
    }

    @computed
    get isMobile(): boolean {
        return this.stores?.screenSize?.isMobile;
    }

    @computed
    get queryStore(): QueryStore {
        return this.stores.query;
    }

    setupDefaultFundCenterContext(): IFundCenterContext {
        const defaultContext = {
            activeFundTypeTab: this.config.selectedFundType.label,
            activeModuleTab: this.config.selectedModule.label,
            activeSearchTerm:
                this.identifierSearch && this.identifierSearch.value,
            activeFilters: this.getAnalyticsActiveFilters(),
            embedInputs: toJS(this.appDataProps),
            appGuid: Manifest.getApplicationGuid() || 'unknown',
        };
        return defaultContext;
    }

    getAnalyticsActiveFilters(): Record<string, string> {
        const analyticsActiveFilters = {};
        const { filter: filterStore } = this.stores;
        const { filters: filterStoreFilters } = filterStore;

        for (const filter of filterStoreFilters) {
            const { property, values, filterSearchKey } = filter;
            for (const value of values) {
                // value is a label/value pair
                if (
                    filterStore.isChecked(
                        property,
                        value.value,
                        filterSearchKey
                    )
                ) {
                    if (analyticsActiveFilters[property]) {
                        analyticsActiveFilters[property].push(value.value);
                    } else {
                        analyticsActiveFilters[property] = [value.value];
                    }
                }
            }
        }
        return analyticsActiveFilters;
    }

    @action
    updateServicesEntityType() {
        const { entityType } = this.config;
        if (this.servicesEntityType !== entityType) {
            this.servicesEntityType = entityType;
        }
    }

    parseEventDetails = <TEvent>(
        event: CustomEvent<TEvent>,
        callback: (eventDetails: TEvent) => void
    ) => {
        const detail = (event as CustomEvent).detail;
        callback(detail);
    };

    // Init function to boot the app
    @action
    init = async props => {
        const loadingId = this.stores.loading.start('global');
        const queryParams = this.getParams(props);

        if (!isNullOrUndefined(props.root)) {
            this.appDataProps = {
                ...this.getAppParams(props.root),
                ...queryParams,
            };
        }

        this.getApiUrl(props);

        this.configServices = new ConfigServices(this.apiUrl);
        this.columnStore = new ColumnStore(this.stores);

        await this.getApplicationAppConfig();
        this.stores.eventBusStore.initialize({
            [models.eventBus.EventType.globalSearchChange]: async event => {
                this.parseEventDetails(event, this.loadSearchFromEvent);
            },
            [models.eventBus.EventType.paginationChange]: async event => {
                this.parseEventDetails(
                    event,
                    this.stores.paginationStore.paginationChange
                );
            },
        });
        await this.stores.eventBusStore.register();
        this.analyticsHelper = new AnalyticsHelper(
            this.setupDefaultFundCenterContext,
            {
                type: this.config.globalConfig.analytics?.type,
            }
        );

        this.updateServicesEntityType();

        // used for testing purposes using other environment data
        const { baseUrl, token } = getParams(
            props.queryString,
            location.search
        ) as Params;

        if (token) {
            // token override from query params
            this.token = token as string;
        }
        // init services
        if (!isNullOrUndefined(this.servicesEntityType)) {
            this.services = new Services(
                this.servicesEntityType,
                this.apiUrl,
                this.token
            );
        }

        // once config is initialized, we need to update the data props culture in case we need the default from the config
        this.appDataProps.culture = this.culture;

        // Set the explicit service url onto the services to improve caching logic
        if (
            this.config &&
            this.config.globalConfig &&
            this.config.globalConfig.serviceUrl
        ) {
            this.services.explicitServiceUrl = this.config.globalConfig.serviceUrl;
        }

        if (baseUrl) {
            this.baseUrl = baseUrl;
            // the services uses the API path, set up the explicit service url here which will overwrite the value from the config
            this.services.explicitServiceUrl = `${this.baseUrl}/services/`;
        }

        this.debouncePostInitializeConfig(async () => {
            await this.getWatchList();
            this.setInitialSort();
            this.addFilterSearch();
            await this.getUserFilters();
            this.addUserFilterSearch();

            await this.getCultures();

            if (this.stores) {
                const {
                    disclaimer: disclaimerStore,
                    translation: translationStore,
                    commentary: commentaryStore,
                } = this.stores;
                const promises: Array<Promise<void>> = [];

                if (disclaimerStore) {
                    disclaimerStore.initialize();
                    promises.push(
                        GlobalDisclaimerStore.load(
                            this.stores,
                            this.config,
                            this.culture
                        )
                    );
                }
                if (commentaryStore) {
                    promises.push(commentaryStore.getGlobalCommentaries());
                }
                if (promises.length > 0) {
                    await Promise.all(promises);
                }

                if (translationStore) {
                    this.getTranslations();
                }
            }

            if (this.config.isDocumentModule) {
                this.documentModule = new DocumentModuleStore(this);
            } else {
                Promise.all([
                    this.getFunds(),
                    this.getFundDetailLists(),
                    this.getActiveFilters(),
                ]);
            }
            this.assetStore = new AssetStore(this.stores, this);
            const promisesToAwait = [this.assetStore.init()];

            this.getFilters();
            this.getDocumentFilters();
            this.getCultureFilters();
            this.getGlobalDocuments();
            this.getClassProperties();

            await Promise.all(promisesToAwait);
            this.isBootstrapped = true;

            // If Intl not supported then add locale data
            if (!props.intlSupported) {
                await this.addLocaleData();
            }

            ModalHelper.init(this.shadowRoot);

            this.stores.loading.stop('global', loadingId);

            setTimeout(() => {
                const fcInfo = {
                    // STANDARD DETAILS
                    templateId: Manifest.ksysAppTemplateId,
                    appGuid: Manifest.getApplicationGuid(),
                    configKey: this.configurationKey,
                    styleKey: 'USING FC THEME',
                    hasData: true, // TODO: We need a method that can work out if the FC has everything it needs to display, defaulting to true for now.
                    // CUSTOM FC Details
                    token: this.token,
                    manifest: Manifest.serialize(),
                    appParams: toJS(this.appDataProps),
                    config: this.config.configFile,
                    theme: this.theme,
                };
                const { eventBusStore } = this.stores;
                // The following debug statement will only display when ?ksysAppDebug is defined in the URL.
                ErrorTracking.log('debug', {
                    debug: {
                        message: 'Fund Center Initialized',
                    },
                    data: fcInfo,
                });
                registerAppLoaded(this.root, this.appDataProps);
                eventBusStore.trigger(
                    models.eventBus.EventType.appInitialized,
                    {
                        detail: fcInfo,
                    }
                );
            }, 0);
        });
    };

    async debouncePostInitializeConfig(
        callback: () => Promise<void>,
        time: number = 100
    ) {
        const { eventBusStore } = this.stores;
        if (time < 15000) {
            setTimeout(async () => {
                if (eventBusStore.requiredCallbacksCompleted) {
                    await callback();
                } else {
                    await this.debouncePostInitializeConfig(
                        callback,
                        time + 100
                    );
                }
            }, 100);
        } else {
            await callback();
        }
    }

    // Sets various values from props or query string parameters
    // Query string parameters take precedence over props
    @action
    getParams = props => {
        const {
            fundlist = props.fundlist,
            culture = props.culture,
            country = props.country,
            tab = props.tab,
            categoryTab = props.categorytab,
            searchTerm = props.searchTerm || '',
            columnGroup = {},
            ...inputParams
        } = getParams(props.queryString, location.search) as Params;

        const {
            inputFundList = fundlist || props.inputFundList,
            inputCulture = culture || props.inputCulture,
            inputCountry = country || props.inputCountry,
            inputTab = tab || props.inputTab,
            inputCategoryTab = categoryTab || props.inputCategoryTab,
            inputSearchTerm = searchTerm || props.inputSearchTerm,
            inputColumnGroup = columnGroup,
        } = inputParams;

        this.filterParams = getFilterParams(props.queryString, location.search);

        const isExpanded =
            this.filterParams.hasOwnProperty(EXPANDED_PARAM) ||
            this.filterParams.hasOwnProperty(INPUT_EXPANDED_PARAM);
        const isExpandAll =
            this.filterParams.hasOwnProperty(EXPAND_ALL_PARAM) ||
            this.filterParams.hasOwnProperty(INPUT_EXPAND_ALL_PARAM);
        this.isExpanded = isExpanded;
        this.isExpandAll = isExpandAll;
        this.fundlist = inputFundList || '';
        this.defaultCulture = inputCulture;
        this.currentModuleId = inputTab;
        this.currentFundType = inputCategoryTab;
        this.columnGroups = inputColumnGroup;
        // We will only set the pageStart and pageLimit if they are supplied in the query string
        // and need to ensure that it is a number

        const paramPageStart =
            this.filterParams[PAGE_START] ||
            this.filterParams[INPUT_PAGE_START];
        const paramPageLimit =
            this.filterParams[PAGE_LIMIT] ||
            this.filterParams[INPUT_PAGE_LIMIT];
        if (paramPageStart && paramPageLimit) {
            const pageStart = Number(this.filterParams[paramPageStart]);
            const pageLimit = Number(this.filterParams[paramPageLimit]);
            if (!isNaN(pageStart) && !isNaN(pageLimit)) {
                this.stores.paginationStore.updatePaging(pageStart, pageLimit);
            }
        }
        if (!isNullOrEmpty(inputSearchTerm)) {
            this.search(inputSearchTerm);
        }

        return {
            fundlist: inputFundList,
            country: inputCountry,
            culture: inputCulture,
            tab: inputTab,
            categoryTab: inputCategoryTab,
            searchTerm: inputSearchTerm,
        };
    };

    // get data properties off root element
    @action
    getAppParams(rootElement) {
        const appParamsFromElement = Object.keys(rootElement.attributes).reduce(
            (existing, key) => {
                let name = snakeCase(rootElement.attributes[key].name);
                // clean data input prefix
                if (name.substring(0, 11).toLowerCase() === 'data_input_') {
                    name = name.substring(11, name.length);
                }
                // clean data prefix
                if (name.substring(0, 5).toLowerCase() === 'data_') {
                    name = name.substring(5, name.length);
                }

                existing[name] =
                    (rootElement &&
                        rootElement.attributes[key] &&
                        rootElement.attributes[key].value) ||
                    null;
                return existing;
            },
            {}
        ) as AppParams;

        return { ...appParamsFromElement };
    }

    // Parses an api url from the app script tag src... but why?!
    // Our services need to make api calls via the clients app instance url, which will be different for each client app.
    // We can't store it in the app config because we would need to know the app url to get it...
    // However, the script tag for the app will use the app url for the main javascript file.
    // E.g. https://api.fundpress.io/applicationManager/apps/xxxx-xxxx-xxxx-xxxx/main.js
    // So we pass the src of our own script tag into the app where we can get parse the app instance url.
    // We can then pass this to our services classes so api calls can hit the app url.
    @action
    getApiUrl = (props: AppProps) => {
        const {
            scriptSrc,
            apiUrl: explicitApiUrl,
            baseUrl: explicitBaseUrl,
        } = props;
        let apiUrl =
            explicitApiUrl ||
            scriptSrc
                .split('/')
                .slice(0, 6)
                .join('/');
        const baseUrl =
            explicitBaseUrl ||
            scriptSrc
                .split('/')
                .slice(0, 3)
                .join('/');

        if (!explicitApiUrl && !apiUrl.includes('applicationManager/apps')) {
            apiUrl = '';
        }

        this.apiUrl = apiUrl;
        this.baseUrl = baseUrl;
    };

    @computed
    get configurationKey() {
        const { configuration_key = 'default' } = this.appDataProps;
        return configuration_key;
    }

    // Gets application config for a token and theme
    @action
    getApplicationAppConfig = async () => {
        try {
            const config = await this.configServices.getApplicationAppConfig(
                this.configurationKey
            );
            this.theme = mergeAppStylesWithTheme(
                config.applicationStyles,
                theme
            );
            try {
                const configFile =
                    process.env.NODE_ENV === 'development'
                        ? developmentAppConfig
                        : config.applicationConfiguration;
                this.config = new ConfigStore(
                    this.stores,
                    configFile.components,
                    this.currentModuleId,
                    this.currentFundType,
                    this.fundlist,
                    this.appDataProps
                );
            } catch (e) {
                console.warn(e);
            }

            this.stores.screenSize.init();
            this.token =
                get(config, 'authentication.token') ||
                get(config, 'applicationConfiguration.token') ||
                get(config, 'applicationConfiguration.apiToken');
        } catch (e) {
            console.warn(e);
        }
    };

    // Gets an Intl polyfill locale data file, e.g: fr-FR.json
    // then makes local data available to polyfilled Intl
    @action
    addLocaleData = async () => {
        try {
            const localeData = await this.configServices.getLocaleFile(
                this.culture
            );
            (window as any).Intl.__addLocaleData(localeData);
        } catch (e) {
            console.warn(e);
        }
    };

    // Updates filter search and document filter search based on filter params from query string.
    // If a filter property matches config.documentGroupMeta (e.g. document_type) then we can add a document filter search.
    //    - when a filter is added to the URL, it should be added to fund & document filters, as we don't specify for which module.
    // Ideally, _prefixed query string parameters would be reserved for filters,
    // but _expanded was already specified to a client, so we now have to handle it.
    @action
    addFilterSearch = () => {
        Object.keys(this.filterParams).map(property => {
            if (!DISALLOWED_FILTER_PARAMS.includes(property)) {
                const values = this.filterParams[property]
                    ? this.filterParams[property].split('|')
                    : [''];

                if (this.config.isDocumentMetaFilter(property)) {
                    this.documentFilterSearch.push({
                        property,
                        values,
                        matchtype: 'MATCH',
                    });
                } else if (this.config.isDocumentInferredFilter(property)) {
                    this.documentFilterInferredSearch.push({
                        property,
                        values,
                        matchtype: 'MATCH',
                    });
                } else if (this.config.isDocumentPropertyFilter(property)) {
                    this.documentPropertySearch.push({
                        property,
                        values,
                        matchtype: 'MATCH',
                    });
                } else {
                    this.filterSearch.push({
                        property,
                        values,
                        matchtype: 'MATCH',
                    });
                }
            }
        });
    };

    @action
    addUserFilterSearch = () => {
        if (this.userFilters) {
            for (const [property, values] of Object.entries(this.userFilters)) {
                const filterConfig = this.config.filtersConfigByProperty[
                    property
                ];
                if (filterConfig && !!filterConfig.saveValueAsUserPreference) {
                    if (this.config.isDocumentMetaFilter(property)) {
                        this.documentFilterSearch.push({
                            property,
                            values,
                            matchtype: 'MATCH',
                        });
                    } else if (this.config.isDocumentInferredFilter(property)) {
                        this.documentFilterInferredSearch.push({
                            property,
                            values,
                            matchtype: 'MATCH',
                        });
                    } else if (this.config.isDocumentPropertyFilter(property)) {
                        this.documentPropertySearch.push({
                            property,
                            values,
                            matchtype: 'MATCH',
                        });
                    } else {
                        this.filterSearch.push({
                            property,
                            values,
                            matchtype: 'MATCH',
                        });
                    }
                }
            }
        }
    };

    // Add configured advancedSort
    // Reset property sort
    @action
    setInitialSort = () => {
        this.advancedSort = this.config.advancedSort;
        this.propertySort = [];
    };

    @action
    getWatchList = async () => {
        await this.stores.watchlist.initialize();
        this.watchList = this.stores.watchlist.getWatchlist(this.watchListKey);
        if (!this.watchListActiveSetByUser) {
            this.watchListActive = !!(
                this.config &&
                this.config.globalWatchlistConfig &&
                this.config.globalWatchlistConfig.activeOnLoad &&
                this.watchList &&
                this.watchList.length > 0
            );
        }
    };

    @action
    getUserFilters = async () => {
        await this.stores.filter.initialize();
        this.userFilters = this.stores.filter.userFilters;
    };

    @action
    toggleWatchList = () => {
        this.watchListActiveSetByUser = true;
        this.watchListActive = !this.watchListActive;
        this.analyticsHelper.logEvent({
            event: 'watchlist_toggle_view',
            eventType: 'click',
            context: {
                active: this.watchListActive ? 'Yes' : 'No',
                fundsInList: this.watchListCount,
            },
        });
    };

    // Toggle sort
    @action
    toggleSort = column => {
        column = getSortableColumn(column);
        const property =
            get(column, 'data.property') || get(column, 'property');

        if (Object.keys(ADVANCED_SORT_TYPES).includes(property)) {
            this.toggleAdvancedSort(column);
        } else {
            this.togglePropertySort(column);
        }
        const direction = this.advancedSort
            ? this.advancedSort.direction
            : this.propertySort[this.propertySort.length - 1].direction;
        this.analyticsHelper.logEvent({
            event: 'apply_sort',
            eventType: 'click',
            context: {
                sortColumn: getColumnLabel(column, this),
                sortDirection: direction,
            },
        });
    };

    // Toggle property sort
    @action
    togglePropertySort = column => {
        // Get sort key
        const key = get(column, 'property') || get(column, 'data.code');

        // Find the selected column in propertySort
        const existingColumn = this.propertySort.find(s => s.key === key);

        // If there is an existing sort, toggle the direction
        // If there is not an existing sort, replace with new sort
        if (existingColumn) {
            existingColumn.direction =
                existingColumn.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : SORT_DIRECTIONS.ASC;

            this.propertySort = [existingColumn];
        } else {
            this.propertySort = [
                {
                    key,
                    direction: SORT_DIRECTIONS.ASC,
                },
            ];
        }

        // If a sort is configured matching fundGroupProperty, add to top of sort array
        if (this.fundGroupPropertySort) {
            this.propertySort.unshift(this.fundGroupPropertySort);
        }

        // Clear advancedSort
        this.advancedSort = undefined;

        // Get data
        this.getFunds();
        this.getFundDetailLists();
    };

    // Toggle advanced sort
    @action
    toggleAdvancedSort = column => {
        // Build advanced search object
        const { property, code, key, sortKey = '' } = column.data;

        const advancedSort = {
            type: ADVANCED_SORT_TYPES[property],
            code,
            key: sortKey,
            value: key,
            direction: SORT_DIRECTIONS.ASC,
        };

        // See if we have already have matching advanced sort
        const existingSort = isExistingAdvancedSort(column, this.advancedSort);

        // If existing advanced sort, toggle the direction
        // If there is not an existing advanced sort, set it
        if (existingSort) {
            this.advancedSort.direction =
                this.advancedSort.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : SORT_DIRECTIONS.ASC;
        } else {
            this.advancedSort = advancedSort;
        }

        // Reset property sort
        this.propertySort = [];

        // Get data
        this.getFunds();
        this.getFundDetailLists();
    };

    // Get class property label from code
    @action
    getClassPropertyLabel = code => {
        const property = this.classPropertiesByCode[code];
        if (property) {
            return property.label;
        }
        return code;
    };

    // Get translations
    @action
    getTranslations = async () => {
        const { translation } = this.stores;
        try {
            const possibleCultures = [this.culture, this.config.defaultCulture];
            const translationCulture = possibleCultures.filter(
                (v, i, a) => a.indexOf(v) === i
            );
            const translationData = await this.services.getTranslation(
                this.config.sourceCulture,
                translationCulture
            );
            translation.setTranslationData(translationData);
        } catch (e) {
            console.warn(e);
        }
    };

    hasMinimumFiltersNeeded = () => {
        const minimumFiltersNeeded =
            this.config &&
            this.config.filterOptions &&
            this.config.filterOptions.minimumFiltersNeeded;
        let hasMinimumFiltersNeeded = true;
        if (minimumFiltersNeeded && minimumFiltersNeeded.length > 0) {
            if (!this.filterSearch) {
                hasMinimumFiltersNeeded = false;
            } else {
                for (const key of minimumFiltersNeeded) {
                    const filterForProperty = this.filterSearch.find(
                        filter => filter.property === key
                    );
                    if (!filterForProperty) {
                        hasMinimumFiltersNeeded = false;
                        break;
                    }
                    const { values } = filterForProperty;
                    if (!values || values.length === 0) {
                        hasMinimumFiltersNeeded = false;
                        break;
                    }
                }
            }
        }
        if (!hasMinimumFiltersNeeded) {
            console.warn(
                'Minimum filters not met: ',
                toJS(minimumFiltersNeeded)
            );
        }
        return hasMinimumFiltersNeeded;
    };

    // Get fund data
    @action
    getFunds = async (paged = false) => {
        if (this.getFundsRunning) {
            return;
        }
        const canExecute = this.hasMinimumFiltersNeeded();
        const hasWatchList =
            !this.watchListActive ||
            (this.watchListActive && this.watchListCount > 0);
        if (!canExecute || !hasWatchList) {
            this.total = 0;
            this.funds = [];
            return;
        }

        this.getWatchlistHierarchy();

        this.getFundsRunning = true;
        // Set start page
        this.start = paged ? this.start + this.config.limit : 0;

        // If loading initial results show main loading
        const loadingTarget: LoadingTarget = paged ? 'funds_more' : 'global';
        const loadingId = this.stores.loading.start(loadingTarget);

        // Remove filter searches not currently in filter properties
        // This ensures that only configured filters and query param filters are applied,
        // whilst retaining filter searches when switching between tabs
        const filterSearch = this.filterSearch.filter(s =>
            this.filterProperties.includes(s.property)
        );
        const search = this.watchListSearchEnabled
            ? filterSearch.concat(this.watchListSearch)
            : filterSearch.concat(this.fundTypeSearch);

        try {
            const sort =
                this.propertySort.length > 0
                    ? this.propertySort
                    : this.config.sort;

            if (!sort && !this.advancedSort) {
                console.warn(
                    'Module Warning:',
                    'searchEntity sort/advancedSort not defined'
                );
            }

            // Note the pageStart and pageLimit is defaulted to null and will only be set via the query string
            const options: GetDataOptions = {
                sort,
                fundList: this.fundLists,
                fundListExclusion: this.fundListExclusions,
                filterSearch: search,
                identifierSearch: this.identifierSearch,
                advancedSort: this.advancedSort,
                documentsJoinSpec: this.documentsJoinSpec,
                start: this.stores.paginationStore.pageStart || this.start,
                limit:
                    this.stores.paginationStore.pageLimit || this.config.limit,
                excludeExpiredDocuments: this.config.excludeExpiredDocuments,
                cultureCode: this.culture,
                sourceCulture: this.config.sourceCulture,
                cultureSearchTransform: this.config.cultureSearchTransform,
                timeseriesInclude: this.config.timeseriesInclude,
                statisticsInclude: this.config.statisticsInclude,
                allocationsInclude: this.config.allocationsInclude,
                disclaimersInclude: this.config.disclaimersInclude,
                commentariesInclude: this.config.commentariesInclude,
                fundManagersInclude: this.config.fundManagersInclude,
                includeProperties: this.config.includeProperties,
            };
            const { total, values } = await this.services.getData(options);

            if (total > 0) {
                this.stores.paginationStore.updatePagination(total);
            }
            // If paged then join to existing data, otherwise set new data
            const funds = paged ? this.funds.concat(values) : values;

            this.funds = funds;

            // Build link filter
            this.buildLinkFilter();

            const computedTotal = paged
                ? Math.max(total, this.funds.length)
                : total;

            this.total = computedTotal;
            this.disclaimerStore.loadModuleDisclaimers();
        } catch (e) {
            console.warn(e);
        }
        this.getFundsRunning = false;
        this.stores.loading.stop(loadingTarget, loadingId);
    };

    getFundDetail = async shareClass => {
        // setup the search entity request
        const requestBody = this.config.rawRowConfig.requestBody;
        if (requestBody) {
            // de ref the config
            const body: ISearchEntityRequest = { ...requestBody };
            // Replace placeholders for joinSpec search values
            if (body.include?.documents) {
                const { joinSpec } = body.include?.documents;
                if (joinSpec) {
                    for (const joinSpecValue of joinSpec) {
                        for (const joinSpecSearch of joinSpecValue.search) {
                            joinSpecSearch.values = joinSpecSearch.values.map(
                                joinSpecSearchValue =>
                                    replacePlaceholders({
                                        text: joinSpecSearchValue,
                                        appParams: this.appDataProps,
                                    })
                            );
                        }
                    }
                }
            }

            // only fetch for given client code.
            body.clientCode = body.clientCode || [shareClass.clientCode];
            body.applyFormats = body.applyFormats || true;
            body.culture = body.culture || this.culture;
            body.sourceCulture =
                body.sourceCulture || this.config.sourceCulture;
            body.disclaimers = body.disclaimers || true;
            body.translate = body.translate || true;
            const { values } = await this.services.getSearchEntity(body);
            return values && values[0];
        } else {
            // if no request body is defined, use the initial shareclass call for the fund card detail
            return shareClass;
        }
    };

    getDocumentsDetail = async (
        shareClass: ShareClass,
        columnDocumentSearchRequests?: DocumentSearchRequests
    ): Promise<Array<ShareClassDocument>> => {
        const {
            documentSearchRequests: rowDocumentSearchRequests,
        } = this.config.rawRowConfig;
        const documentSearchRequests =
            columnDocumentSearchRequests || rowDocumentSearchRequests;
        if (documentSearchRequests) {
            const { inputs = [], requests = [] } = toJS(documentSearchRequests);

            // Get input keys and query values in an object
            const inputValues = inputs.reduce((inputDictionary, input) => {
                const { key, value } = input;
                const { query } = this.documentSearchInputsQueryResults(
                    { query: value },
                    shareClass
                );

                inputDictionary[key] = query;

                return inputDictionary;
            }, {});

            // Iterate over search request config to replace placeholders in search values
            // and build an array of the request promises
            const requestPromises = requests.reduce((requestItems, request) => {
                if (request.search) {
                    request.search = request.search.map(search => {
                        search.values = this.replaceSearchPlaceholders(
                            search,
                            inputValues
                        );
                        return search;
                    });
                }

                if (request.entitySearch) {
                    request.entitySearch = request.entitySearch.map(search => {
                        search.values = this.replaceSearchPlaceholders(
                            search as EntitySearch,
                            inputValues
                        );
                        return search;
                    });
                }

                if (request.searchEntityRequest) {
                    request.searchEntityRequest.search = request.searchEntityRequest.search.map(
                        search => {
                            search.values = this.replaceSearchPlaceholders(
                                search,
                                inputValues
                            );
                            return search;
                        }
                    );
                    if (request.searchEntityRequest.fundList) {
                        request.searchEntityRequest.fundList = this.replaceFundListPlaceholders(
                            request.searchEntityRequest.fundList,
                            inputValues
                        );
                    }
                    if (request.searchEntityRequest.fundListExclusion) {
                        request.searchEntityRequest.fundListExclusion = this.replaceFundListPlaceholders(
                            request.searchEntityRequest.fundListExclusion,
                            inputValues
                        );
                    }
                }

                const requestPromise = this.services.getDocuments({
                    ...request,
                });

                requestItems.push(requestPromise);

                return requestItems;
            }, []);

            // Await the document requests
            const documentResponses = await Promise.all(requestPromises);

            // Merge in the entity mapping into documentEntityData
            for (const documentResponse of documentResponses) {
                const {
                    documentEntities,
                    documentJoinMap,
                } = this.documentEntityData;

                const rawDocumentEntities: IEntityData =
                    documentResponse.entities;
                if (rawDocumentEntities) {
                    for (const [entityId, entity] of Object.entries(
                        rawDocumentEntities
                    )) {
                        if (!documentEntities[entityId]) {
                            documentEntities[entityId] = entity;
                        }
                    }
                }

                const rawDocumentJoinMap: IJoinMap = documentResponse.joinMap;
                if (rawDocumentJoinMap) {
                    for (const [
                        documentClientCode,
                        linkedEntitiesIds,
                    ] of Object.entries(rawDocumentJoinMap)) {
                        if (!documentJoinMap[documentClientCode]) {
                            documentJoinMap[documentClientCode] = [];
                        }
                        for (const linkedEntitiesId of linkedEntitiesIds) {
                            if (
                                !documentJoinMap[documentClientCode].includes(
                                    linkedEntitiesId
                                )
                            ) {
                                documentJoinMap[documentClientCode].push(
                                    linkedEntitiesId
                                );
                            }
                        }
                    }
                }
            }

            // Merge all the documents to a single array
            const documents = documentResponses.reduce(
                (documentValues, document) =>
                    documentValues.concat(document.values),
                []
            );

            return documents;
        }

        return [];
    };

    applyReplacePlaceholders(
        text: string,
        inputValues: Record<string, string>
    ) {
        if (text.includes('{')) {
            return replacePlaceholders({
                text,
                values: inputValues,
                appParams: this.appDataProps,
            });
        }
        return text;
    }

    replaceFundListPlaceholders(
        fundList: string | Array<string>,
        inputValues: Record<string, string>
    ) {
        let text = isArrayLike(fundList) ? fundList.join('|') : fundList;
        text = this.applyReplacePlaceholders(text, inputValues);
        if (isArrayLike(fundList)) {
            return text.split('|');
        }
        return text;
    }

    replaceSearchPlaceholders(
        search: DocumentSearch | EntitySearch,
        inputValues: Record<string, string>
    ) {
        let searchValues = search.values.join('|');
        searchValues = this.applyReplacePlaceholders(searchValues, inputValues);
        return searchValues.split('|');
    }

    documentSearchInputsQueryResults(
        query: models.query.IQueryKeyValue,
        shareClass: ShareClass
    ) {
        const { culture } = this;
        const { entityType } = this.config;
        const queryContext: IQueryContext = {
            entityByType: {
                [entityType]: shareClass,
            },
        };

        return getQueryResults(queryContext, query, culture, this.appDataProps);
    }

    // get fund detail lists from fund list filters
    @action
    getFundDetailLists = async () => {
        if (this.getFundDetailListsRunning) {
            return;
        }
        this.getFundDetailListsRunning = true;
        const fundDetailLists: any = {};
        const fundListFilters = this.fundListFilters;
        const identifierProperty = this.config.identifierProperty;
        if (!isNullOrEmpty(fundListFilters)) {
            // Remove filter searches not currently in filter properties
            // This ensures that only configured filters and query param filters are applied,
            // whilst retaining filter searches when switching between tabs
            const filterSearch = this.filterSearch.filter(s =>
                this.filterProperties.includes(s.property)
            );

            const search = this.watchListSearchEnabled
                ? filterSearch.concat(this.watchListSearch)
                : filterSearch.concat(this.fundTypeSearch);

            for (const {
                key,
                fundList,
                fundListExclusion,
            } of fundListFilters) {
                try {
                    const {
                        values,
                    } = await this.services.getDataSimplified(
                        fundList,
                        fundListExclusion,
                        search,
                        this.identifierSearch,
                        {},
                        [identifierProperty]
                    );

                    fundDetailLists[key] = values.map(value =>
                        this.getEntityProperty(value, identifierProperty)
                    );
                } catch (e) {
                    console.warn(e);
                }
            }
        }
        this.fundDetailLists = fundDetailLists;
        this.getFundDetailListsRunning = false;
    };

    @action
    getHistoricData = async (requestConfig: HistoricDataRequestConfig) => {
        if (this.getHistoricDataRunning || !requestConfig) {
            return;
        }
        this.getHistoricDataRunning = true;
        let result: any = {};

        try {
            let body: ISearchEntityRequest = {};
            if (requestConfig.requestBody) {
                // setup the search entity request
                body = requestConfig.requestBody;
            } else {
                // get properties that should be included in the api call
                const properties =
                    requestConfig.columns &&
                    requestConfig.columns
                        .filter(
                            column =>
                                column.data &&
                                (column.data as DataLocator).property ===
                                    'properties_pub'
                        )
                        .map(column => (column.data as DataLocator).code);

                // data could be a single item or an array, set up codes for api call
                const timeseriesCodes: Array<string> = [];
                if (isArrayLike(requestConfig.data)) {
                    timeseriesCodes.push(
                        ...(requestConfig.data as Array<DataLocator>).map(
                            data => data.code
                        )
                    );
                } else {
                    timeseriesCodes.push(
                        (requestConfig.data as DataLocator).code
                    );
                }

                // setup the search entity request
                body = {
                    type: this.config.entityType,
                    search: [],
                    include: {
                        timeseries: {
                            limitTo: timeseriesCodes,
                        },
                    },
                    preserveOriginal: true,
                    includeProperties: properties,
                } as any;
            }
            if (requestConfig.clientCode) {
                body.clientCode = [requestConfig.clientCode];
            }
            const { values } = await this.services.getSearchEntity(body);

            result = values;
        } catch (e) {
            console.warn(e);
        }
        this.getHistoricDataRunning = false;
        return result;
    };

    @action
    buildLinkFilter = async () => {
        const fundLinkFilter = {};

        // get global config
        if (this.config.globalDownloadControlActions) {
            this.updateLinkFilterForActions(
                fundLinkFilter,
                this.config.globalDownloadControlActions
            );
        }

        // get fund header config
        if (this.config.fundGroupHeader?.actions) {
            this.updateLinkFilterForActions(
                fundLinkFilter,
                this.config.fundGroupHeader.actions
            );
        }

        // get column config
        for (const column of this.config.columnConfig) {
            await this.setFundLinkFilter(
                column.url,
                column.key,
                fundLinkFilter
            );
            if (column.actions) {
                await this.updateLinkFilterForActions(
                    fundLinkFilter,
                    column.actions
                );
            }
        }

        // get row config
        const rowConfig = this.config.rawRowConfig;
        if (rowConfig) {
            if (rowConfig.header?.url) {
                await this.setFundLinkFilter(
                    rowConfig.header.url,
                    rowConfig.header.key,
                    fundLinkFilter
                );
            }
            if (this.config.documentsGrid?.actions?.start) {
                await this.updateLinkFilterForActions(
                    fundLinkFilter,
                    this.config.documentsGrid.actions.start
                );
            }
            if (this.config.documentsGrid?.actions?.end) {
                await this.updateLinkFilterForActions(
                    fundLinkFilter,
                    this.config.documentsGrid.actions.end
                );
            }
            if (this.config.documentsGridConditionals?.length > 0) {
                for (const docGridConditional of this.config
                    .documentsGridConditionals) {
                    if (docGridConditional.config?.actions?.start) {
                        await this.updateLinkFilterForActions(
                            fundLinkFilter,
                            docGridConditional.config.actions.start
                        );
                    }
                    if (docGridConditional.config?.actions?.end) {
                        await this.updateLinkFilterForActions(
                            fundLinkFilter,
                            docGridConditional.config.actions.end
                        );
                    }
                }
            }
        }

        this.fundLinkFilter = fundLinkFilter;
    };

    @action
    updateLinkFilterForActions = async (
        fundLinkFilter: Record<string, Array<string>>,
        actions: DownloadControlActions
    ) => {
        if (actions.elements?.length > 0) {
            for (const element of actions.elements) {
                if (element.type === 'link' && element._key !== undefined) {
                    await this.setFundLinkFilter(
                        element.linkId || element.linkFilter,
                        element._key,
                        fundLinkFilter
                    );
                }
            }
        }
    };

    @action
    setFundLinkFilter = async (
        urlConfig: string | LinkFilter,
        key: string,
        fundLinkFilter: Record<string, Array<string>>
    ) => {
        const url: string | LinkFilter = this.getUrl(urlConfig);
        if (url && (url as LinkFilter).linkSegmentationFilter) {
            const allowedValues: Array<any> = await this.getFilteredFunds(
                url as LinkFilter
            );
            fundLinkFilter[key] = allowedValues.map(value => value.clientCode);
        }
    };

    @action
    getUrl(configUrl: string | LinkFilter) {
        return this.config.selectedModule.config.links &&
            this.config.selectedModule.config.links[configUrl as string]
            ? this.config.selectedModule.config.links[configUrl as string]
            : (configUrl as LinkFilter);
    }

    @action
    getFilteredFunds = async (url: LinkFilter) => {
        const segmentation = new Segmentation(url.linkSegmentationFilter, this);
        const inclusionClauses = segmentation.orchestrateList(
            url.linkSegmentationFilter.inclusionClauses
        );
        const exclusionClauses = segmentation.orchestrateList(
            url.linkSegmentationFilter.exclusionClauses
        );

        if (!isNullOrUndefined(inclusionClauses)) {
            inclusionClauses.forEach((element, index) => {
                inclusionClauses[index] = replacePlaceholders({
                    text: element,
                    appParams: this.appDataProps,
                });
            });
        }

        if (!isNullOrUndefined(exclusionClauses)) {
            exclusionClauses.forEach((element, index) => {
                exclusionClauses[index] = replacePlaceholders({
                    text: element,
                    appParams: this.appDataProps,
                });
            });
        }

        const clientCodes = this.funds.map(fund => fund.clientCode);

        const options: GetDataOptions = {
            fundList: inclusionClauses || [],
            fundListExclusion: exclusionClauses || [],
            filterSearch: [],
            start: 0,
            limit: 0,
            cultureSearchTransform: this.config.cultureSearchTransform,
            clientCode: clientCodes,
            include: {},
            preserveOriginal: true,
            translate: false,
            applyFormats: false,
            disclaimers: false,
            includeProperties: [],
        };
        // Clean out params that are not needed. set includeProperties to empty array to remove properties_pub from the response.
        const { values: allowedValues } = await this.services.getData(options);
        return allowedValues;
    };

    // Get watchlist hierarchy data
    @action
    clearWatchlistHierarchy = () => {
        this.watchlistHierarchy = undefined;
    };

    @action
    getWatchlistHierarchy = async () => {
        if (!this.watchlistHierarchy) {
            const search = this.fundTypeSearch;

            try {
                const includeProperties: Array<string> = [];
                if (
                    this.config &&
                    this.config.explorer &&
                    this.config.explorer.watchlist &&
                    this.config.explorer.watchlist.property &&
                    this.config.fundGroupHeader
                ) {
                    const {
                        fundGroupProperty,
                        fundSubGroupProperty,
                    } = this.config.fundGroupHeader;
                    if (fundGroupProperty) {
                        includeProperties.push(fundGroupProperty);
                    }
                    if (fundSubGroupProperty) {
                        includeProperties.push(fundSubGroupProperty);
                    }
                    if (includeProperties.length > 0) {
                        includeProperties.push(this.config.watchListProperty);
                    }
                }

                if (includeProperties.length > 0) {
                    const options: GetDataOptions = {
                        fundList: this.fundLists,
                        fundListExclusion: this.fundListExclusions,
                        filterSearch: search,
                        start: 0,
                        limit: 0,
                        cultureCode: this.culture,
                        sourceCulture: this.config.sourceCulture,
                        includeProperties,
                        preserveOriginal: true,
                        translate: !this.config?.explorer?.watchlist
                            ?.disableTranslate,
                        applyFormats: !this.config?.explorer?.watchlist
                            ?.disableFormats,
                    };

                    const { values } = await this.services.getData(options);

                    // Load up the hierarchy to speed up queries

                    this.watchlistHierarchy = this.buildWatchlistHierarchy(
                        values
                    );
                }
            } catch (e) {
                console.warn(e);
            }
        }
    };

    buildWatchlistHierarchy = (
        entities: Array<IEntity>
    ): TWatchlistHierarchy | undefined => {
        const { fundGroupHeader } = this.config;
        if (fundGroupHeader) {
            const { fundGroupProperty, fundSubGroupProperty } = fundGroupHeader;
            if (fundGroupProperty) {
                const groupedByRoot = groupBy(entities, entity => {
                    return get(
                        entity,
                        `properties_pub.${fundGroupProperty}.value`,
                        ''
                    );
                });
                if (fundSubGroupProperty) {
                    const keys = Object.keys(groupedByRoot);
                    keys.forEach(key => {
                        const rootEntities = groupedByRoot[key];
                        groupedByRoot[key] = groupBy(rootEntities, entity => {
                            return get(
                                entity,
                                `properties_pub.${fundSubGroupProperty}.value`,
                                ''
                            );
                        });
                        groupedByRoot[key]._all = rootEntities;
                    });
                }
                return groupedByRoot;
            }
        }
        return undefined;
    };

    handleDownloadDisclaimers(
        disclaimerType: ExplorerDownloadDisclaimerSection,
        moduleCollection: Array<any>
    ) {
        const response: Array<string> = [];
        if (disclaimerType) {
            const {
                useModuleDisclaimers: useModuleTypes,
                order,
                types,
            } = disclaimerType;
            // Default the order to show disclaimers from the module first
            if (useModuleTypes && (!order || order === 'moduleFirst')) {
                response.push(...moduleCollection);
            }
            if (types && Array.isArray(toJS(types))) {
                response.push(...GlobalDisclaimerStore.getSome(types));
            }
            if (useModuleTypes && order === 'explicitFirst') {
                response.push(...moduleCollection);
            }
        }
        return response;
    }

    downloadExplorerLogAnalytics(filename: string) {
        const analyticsContext = {
            event: 'document_download',
            eventType: 'click',
            context: {
                filename,
                language: languageFromCulture(this.culture),
                type: 'csv',
            },
        };
        this.analyticsHelper.logEvent(analyticsContext);
    }

    @action
    logDataLayer(
        event: string,
        context: object,
        eventType: string = 'userActioned'
    ) {
        const analyticsContext: IAnalytics<IFundCenterContext> = {
            event,
            eventType,
            context: {
                ...context,
            },
        };
        this.analyticsHelper.logEvent(analyticsContext);
    }

    @action
    downloadTable = async () => {
        const { loading: loadingStore } = this.stores;
        const loadingId = loadingStore.getLoadingId();
        loadingStore.start('explorer_download', loadingId);
        const explorerDownload =
            this.config.explorer && this.config.explorer.download;
        if (explorerDownload && explorerDownload.enabled) {
            const { type = ExplorerDownloadType.csv } = explorerDownload;
            if (type === ExplorerDownloadType.csv) {
                await this.downloadExplorerTableCsv(explorerDownload);
            }
            if (type === ExplorerDownloadType.pdf) {
                await this.downloadExplorerTablePdf(explorerDownload);
            }
        }
        loadingStore.stop('explorer_download', loadingId);
    };

    getPdfUrl = (options: ExplorerDownloadPdf) => {
        const { url = {} } = options;
        const {
            origin,
            pathname,
            queryStringOptions,
            uiInputs,
            queryInputs,
        } = url;

        const { filter: filterStore } = this.stores;
        const params = filterStore.getFilterParamsForQueryString(
            queryStringOptions
        );
        const inputs = {};

        this.loadDataStoreQueries(inputs, uiInputs);
        this.loadQueries(inputs, queryInputs);

        const searchTerms = Object.keys(params).map(key => {
            return `${key}=${params[key]}`;
        });
        const search = searchTerms.join('&');
        let responseUrl = `${origin}${pathname}?${search}`;
        responseUrl = this.replacePlaceholders(responseUrl, inputs, 'input');
        return { params, url: responseUrl };
    };

    @action
    downloadExplorerTablePdf = async (explorerDownload: ExplorerDownload) => {
        const {
            pdfDownloadOptions: globalPdfOptions = {},
        } = this.config.globalConfig;
        const { pdfOptions = {} } = explorerDownload;

        const options: ExplorerDownloadPdf = utils.object.deepMergeObjects(
            globalPdfOptions,
            pdfOptions
        );

        const urlDetails = this.getPdfUrl(options);
        const { url, params } = urlDetails;
        const { printSettings = {}, mode = 'download' } = options;
        if (mode === 'download') {
            const body: models.portal.ExportToPDFRequest = {
                url,
                printSettings: {
                    printEngine: 'Type 2',
                    paperSize: 'Letter',
                    paperMargin: 'none',
                    landscape: false,
                    printBackground: true,
                    ...printSettings,
                },
                type: 'pdf',
                service: 'cloud',
            };
            const pdf = await this.services.generatePdf(body);
            const filename = this.getFileNameForPdfDownload(options, params);
            this.downloadExplorerLogAnalytics(filename);
            await this.downloadPdfResponse(pdf, filename);
        } else if (mode === 'redirect') {
            Object.assign(document.createElement('a'), {
                target: '_blank',
                href: url,
            }).click();
        }
    };

    getFileNameForPdfDownload = (
        options: ExplorerDownloadPdf,
        params: { [key: string]: any }
    ) => {
        const { fileName = {} } = options;
        const { value = `Fund Center Export`, queryInputs } = fileName;
        let response = value;
        const inputs = {};

        this.loadQueries(inputs, queryInputs);
        response = this.replacePlaceholders(response, inputs, 'input');
        response = this.replacePlaceholders(
            response,
            params,
            'param',
            decodeURIComponent
        );

        if (!response.endsWith('.pdf')) {
            response += '.pdf';
        }
        return response;
    };

    replacePlaceholders = (
        value: string,
        inputs: {},
        prefix: string,
        valueTransform?: (value: string) => any
    ) => {
        let response = value;
        Object.keys(inputs).forEach(key => {
            let inputValue = inputs[key];
            if (valueTransform) {
                inputValue = valueTransform(inputValue);
            }
            response = response.split(`{${prefix}:${key}}`).join(inputValue);
        });
        return response;
    };

    async downloadPdfResponse(response: Response, filename: string) {
        const blob = await response.blob();
        // Create an object URL for the blob object
        const url = URL.createObjectURL(blob);

        // Create a new anchor element
        const a = document.createElement('a');

        // Set the href and download attributes for the anchor element
        // You can optionally set other attributes like `title`, etc
        // Especially, if the anchor element will be attached to the DOM
        a.href = url;
        a.download = filename || 'download';

        // Click handler that releases the object URL after the element has been clicked
        // This is required for one-off downloads of the blob content
        const clickHandler = () => {
            setTimeout(() => {
                URL.revokeObjectURL(url);
                a.removeEventListener('click', clickHandler);
            }, 150);
        };

        // Add the click event listener on the anchor element
        // Comment out this line if you don't want a one-off download of the blob content
        a.addEventListener('click', clickHandler, false);

        // Programmatically trigger a click on the anchor element
        // Useful if you want the download to happen automatically
        // Without attaching the anchor element to the DOM
        // Comment out this line if you don't want an automatic download of the blob content
        a.click();

        // Return the anchor element
        // Useful if you want a reference to the element
        // in order to attach it to the DOM or use it in some other way
        return a;
    }

    loadDataStoreQueries = (inputs: object, queries: Array<UiInputQuery>) => {
        if (queries && queries.length > 0) {
            for (const query of queries) {
                this.loadDataStoreQuery(inputs, query);
            }
        }
    };

    loadDataStoreQuery = (instance: object, query: UiInputQuery): any => {
        const { key } = query;
        let value = query.value;

        if (typeof value === 'undefined') {
            if (query.type === UiInputQueryType.columnGroupKey) {
                const columnKey = query.options.columnKey;
                const group = this.columnStore.getSelectedGroup(columnKey);
                if (group) {
                    value = group.key;
                }
            }
        }
        instance[key] = value;
    };

    loadQueries = (inputs: object, queries?: Array<IQueryOptions>) => {
        if (queries && queries.length > 0) {
            for (const query of queries) {
                this.loadQuery(inputs, query);
            }
        }
    };

    loadQuery = (instance: object, query: IQueryOptions): any => {
        if (query && query.queryId) {
            const executionOptions = this.stores.query.getExecutionOptionsUsingContext(
                {}
            );
            const value = this.stores.query.execute(query, executionOptions);
            instance[query.queryId] = value;
        }
    };

    @action
    downloadExplorerTableCsv = async (explorerDownload: ExplorerDownload) => {
        const { disclaimerSections: disclaimerTypes } = explorerDownload;
        const { translation } = this.stores;
        await this.getFundsForCsv();

        let headerDisclaimers = [];
        let footerDisclaimers = [];
        if (disclaimerTypes) {
            const { header, footer } = disclaimerTypes;
            headerDisclaimers = this.handleDownloadDisclaimers(
                header,
                this.headerDisclaimers
            );
            footerDisclaimers = this.handleDownloadDisclaimers(
                footer,
                this.footerDisclaimers
            );
        }
        const columns = [...this.columnStore.columns];

        const {
            showGroup,
            subGroupLabel,
            groupLabel,
        } = this.config.explorer.download;
        const {
            fundGroupProperty,
            fundSubGroupProperty,
        } = this.config.fundGroupHeader;

        if (showGroup && this.config.fundGroupHeader) {
            if (!isNullOrEmpty(fundSubGroupProperty)) {
                const fundSubGroupColumn: ColumnConfig = {
                    label: subGroupLabel,
                    property: fundSubGroupProperty,
                };
                columns.unshift(fundSubGroupColumn);
            }
            if (!isNullOrEmpty(fundGroupProperty)) {
                const fundGroupColumn: ColumnConfig = {
                    label: groupLabel,
                    property: fundGroupProperty,
                };
                columns.unshift(fundGroupColumn);
            }
        }

        const options = {
            headerDisclaimers,
            footerDisclaimers,
            explorerDownload,
            dataStore: this,
            config: this.config,
            columnConfig: { columns },
            shareClasses: this.fundsForCsv,
            locale: this.culture,
            translationStore: translation,
        };
        const csv = CsvHelper.convertTableToCsv(options);
        const fileNameParts: Array<string> = [];
        const selectedFundType = this.config.selectedFundType;
        if (selectedFundType) {
            if (this.config.showFundTypes) {
                fileNameParts.push(selectedFundType.label);
            }
            const selectedModule = this.config.selectedModule;
            if (selectedModule) {
                fileNameParts.push(selectedModule.label);
            }
        } else {
            fileNameParts.push('Export');
        }
        const { filter: filterStore } = this.stores;
        if (filterStore) {
            const { minimumFiltersNeeded } = filterStore;
            if (
                isArrayLike(minimumFiltersNeeded) &&
                minimumFiltersNeeded.length > 0 &&
                this.filterSearch
            ) {
                minimumFiltersNeeded.forEach(filterProperty => {
                    const searchParam = this.filterSearch.find(
                        f => f.property === filterProperty
                    );
                    if (searchParam && isArrayLike(searchParam.values)) {
                        fileNameParts.push(...searchParam.values);
                    }
                });
            }
        }
        const now = new Date();
        const year = now.getFullYear();
        const month = padStart(now.getMonth() + 1, 2);
        const day = padStart(now.getDate(), 2);

        fileNameParts.push(`${year}${month}${day}`);
        const fileName =
            fileNameParts.map(part => translate(translation, part)).join('_') +
            '.csv';
        this.downloadExplorerLogAnalytics(fileName);
        this.downloadCsv(csv, fileName);
    };

    @action
    getFundsForCsv = async () => {
        // If loading initial results show main loading
        const loadingId = this.stores.loading.start('global');

        // Remove filter searches not currently in filter properties
        // This ensures that only configured filters and query param filters are applied,
        // whilst retaining filter searches when switching between tabs
        const filterSearch = this.filterSearch.filter(s =>
            this.filterProperties.includes(s.property)
        );

        const search = this.watchListSearchEnabled
            ? filterSearch.concat(this.watchListSearch)
            : filterSearch.concat(this.fundTypeSearch);

        try {
            const options: GetDataOptions = {
                fundList: this.fundLists,
                fundListExclusion: this.fundListExclusions,
                filterSearch: search,
                identifierSearch: this.identifierSearch,
                sort:
                    this.propertySort.length > 0
                        ? this.propertySort
                        : this.config.sort,
                advancedSort: this.advancedSort,
                documentsJoinSpec: undefined,
                start: 0,
                limit: 0,
                cultureCode: this.culture,
                sourceCulture: this.config.sourceCulture,
                cultureSearchTransform: this.config.cultureSearchTransform,
                timeseriesInclude: this.config.timeseriesInclude,
                statisticsInclude: this.config.statisticsInclude,
                allocationsInclude: this.config.allocationsInclude,
                disclaimersInclude: this.config.disclaimersInclude,
                commentariesInclude: this.config.commentariesInclude,
                fundManagersInclude: this.config.fundManagersInclude,
            };
            const { values } = await this.services.getData(options);

            // If paged then join to existing data, otherwise set new data
            this.fundsForCsv = values;
        } catch (e) {
            console.warn(e);
        }

        this.stores.loading.stop('global', loadingId);
    };

    // Set search term
    @action
    search = value => {
        this.identifierSearchValue = value;
    };

    // Clear search term
    @action
    clearSearch = () => {
        this.identifierSearchValue = '';
    };

    // Clear filters
    @action
    clearFilters = () => {
        this.filterSearch = [];
        this.documentFilterSearch = [];
        this.documentFilterInferredSearch = [];
        this.documentPropertySearch = [];
    };

    @action
    clearFiltersOfType = (
        property: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const updatedSearch = toJS(this[filterSearchKey]);
        this[filterSearchKey] = updatedSearch.filter(
            search => search.property !== property
        );
    };

    // Get values for specified properties limited by any allowed values specified in filter config
    @action
    getFilters = async () => {
        if (this.filterProperties.length > 0) {
            try {
                let filters = await this.services.getFilters(
                    this.filterProperties,
                    this.fundTypeSearch,
                    this.fundLists,
                    this.fundListExclusions
                );
                filters = Object.keys(filters).reduce(
                    (availableFilters, filter) => {
                        if (!this.config.isDocumentFilter(filter)) {
                            const {
                                aliases,
                                allowedValues,
                            } = this.getFilterProps(
                                this.config.filtersConfig,
                                filter
                            );

                            availableFilters[filter] = filters[filter]
                                .filter(f =>
                                    allowedValues.length > 0
                                        ? allowedValues.includes(f.value)
                                        : true
                                )
                                .map(f => ({
                                    label: get(aliases, f.label, f.label),
                                    value: f.value,
                                }));
                        }

                        return availableFilters;
                    },
                    {}
                );
                this.filters = { ...this.filters, ...filters };
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get values for specified properties limited by filter property selections
    @action
    getActiveFilters = async () => {
        if (this.filterProperties.length > 0) {
            const search = this.watchListSearchEnabled
                ? this.filterSearch.concat(this.watchListSearch)
                : this.filterSearch;
            const correctedSearch = this.correctSearchValues(toJS(search));

            try {
                this.activeFilters = await this.services.getFilters(
                    this.filterProperties,
                    correctedSearch,
                    this.fundLists,
                    this.fundListExclusions
                );
            } catch (e) {
                console.warn(e);
            }
        }
    };

    @action
    correctSearchValues = searches => {
        return searches.map(search => {
            search.values = search.values.map(value =>
                this.applyFilterValueDataType(search.property, value)
            );
            return search;
        });
    };

    @action
    applyFilterValueDataType = (
        property: string,
        value: string
    ): string | number | boolean | [] => {
        const config = this.config.filtersConfig.find(
            filterConfig => filterConfig.property === property
        );
        const dataType = (config && config.filterValueDataType) || 'string';
        let returnValue: any = value;
        if (typeof value !== dataType) {
            switch (dataType) {
                case 'string':
                    returnValue = value.toString();
                    break;
                case 'number':
                    returnValue = parseInt(value, 10);
                    break;
                case 'boolean':
                    // Will output false for what js considers falsy values https://developer.mozilla.org/en-US/docs/Glossary/Falsy
                    // Will output true for anything else
                    returnValue = !!value;
                    break;
            }
        }
        return returnValue;
    };

    // Get values for specified document meta, limited by any allowed values specified in filter config
    // Label is replaced by value for consistency with fund/getFilters
    @action
    getDocumentFilters = async () => {
        if (
            this.documentFilterCodes.length > 0 ||
            this.documentFilterInferredCodes.length > 0
        ) {
            try {
                let filters = await this.services.getDocumentFilters(
                    [
                        ...this.documentFilterCodes,
                        ...this.documentFilterInferredCodes,
                    ],
                    this.documentFilterAllowedValuesSearch
                );
                filters = Object.keys(filters).reduce(
                    (availableFilters, filter) => {
                        if (this.config.isDocumentFilter(filter)) {
                            const { aliases } = this.getFilterProps(
                                this.config.filtersConfig,
                                filter
                            );

                            const languagesAdded = [];
                            const labelAliases = [];
                            for (const f of filters[filter]) {
                                const values = Array.isArray(f.value)
                                    ? f.value
                                    : [f.value];
                                const labels = f.label
                                    .replace(/\[|"|\]/g, '')
                                    .split(',');
                                values.map((value, idx) => {
                                    if (languagesAdded.indexOf(value) === -1) {
                                        languagesAdded.push(value);
                                        labelAliases.push({
                                            label: get(
                                                aliases,
                                                labels[idx],
                                                labels[idx]
                                            ),
                                            value,
                                        });
                                    }
                                });
                            }
                            availableFilters[filter] = labelAliases;
                        }

                        return availableFilters;
                    },
                    {}
                );
                this.filters = { ...this.filters, ...filters };
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get property metadata
    @action
    getClassProperties = async () => {
        let hasPropertyPlaceholder = false;
        const identifierSearch = this.identifierSearch;
        if (identifierSearch && identifierSearch.placeholder) {
            const { format, badge } = identifierSearch.placeholder;
            const checkFormatUsesProperties = (
                localizationFormat?: LocalizationFormat
            ) => {
                if (localizationFormat) {
                    const { singular = '', plural = '' } = localizationFormat;
                    if (
                        singular.includes('{properties}') ||
                        plural.includes('{properties}')
                    ) {
                        return true;
                    }
                }
                return false;
            };
            hasPropertyPlaceholder =
                checkFormatUsesProperties(format) ||
                (badge && checkFormatUsesProperties(badge.format));
        }

        if (
            hasPropertyPlaceholder &&
            !isNullOrUndefined(this.classPropertiesByCode)
        ) {
            try {
                const values = await this.services.getProperties(
                    this.config.entityType
                );
                this.classPropertiesByCode = {};
                for (const value of values) {
                    this.classPropertiesByCode[value.code] = value;
                }
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get global documents as per globalDocumentSearch
    @action
    getGlobalDocuments = async () => {
        this.globalDocuments = [];
        const { globalDocumentSearch: search } = this.config;
        if (search.length > 0) {
            try {
                // Replace placeholders for joinSpec search values
                for (const globalDocumentSearchValue of search) {
                    globalDocumentSearchValue.values = globalDocumentSearchValue.values.map(
                        joinSpecSearchValue =>
                            replacePlaceholders({
                                text: joinSpecSearchValue,
                                appParams: this.appDataProps,
                            })
                    );
                }
                const { values } = await this.services.getDocuments({
                    search,
                });
                this.globalDocuments = values;
            } catch (e) {
                console.warn(e);
            }
        }
    };

    // Get system cultures
    @action
    getCultures = async () => {
        try {
            this.cultures = await this.services.getCultures();
        } catch (e) {
            console.warn(e);
        }
    };

    // Add cultureCode to filters in the same format as returned by getFilters for entities and documents.
    // cultureCode: [{ label: 'English', value: 'en-GB' }]
    // This enables us to handle selections in the same way as other filters.
    // Also alias labels as defined in config.cultureAliases (takes precedence) or filter.valueAliases
    @action
    getCultureFilters = async () => {
        const { aliases, allowedValues } = this.getFilterProps(
            this.documentPropertyFilters,
            'cultureCode'
        );

        const cultureFilters = {
            cultureCode: this.cultures
                .filter(culture =>
                    allowedValues.length > 0
                        ? allowedValues.includes(culture.culture)
                        : true
                )
                .map(culture => ({
                    label:
                        get(this.config.cultureAliases, culture.culture) ||
                        get(aliases, culture.culture, culture.culture),
                    value: culture.culture,
                })),
        };
        this.filters = { ...this.filters, ...cultureFilters };
    };

    getAliases(
        valueAliases: Aliases,
        globalValueAliases: keyof GlobalValueAliases
    ) {
        const { globalConfig } = this.config;
        if (valueAliases) {
            return valueAliases;
        }

        if (
            globalConfig &&
            globalConfig.valueAliases &&
            globalConfig.valueAliases[globalValueAliases]
        ) {
            return globalConfig.valueAliases[globalValueAliases];
        }
    }

    getFilterProps(filters: Array<Filter>, filterProperty: string) {
        const { globalValueAliases, valueAliases, allowedValues = [] } =
            filters.find(f => f.property === filterProperty) || {};

        const aliases = this.getAliases(valueAliases, globalValueAliases);

        return {
            aliases,
            allowedValues,
        };
    }

    getTextFromDocumentDataFields(
        document,
        dataTextMapping: DataTextMapping,
        getDefaultText?: () => string
    ) {
        if (
            dataTextMapping &&
            dataTextMapping.dataFields &&
            dataTextMapping.text
        ) {
            const { dataFields, text } = dataTextMapping;
            const values = Object.keys(dataFields).reduce((acc, key) => {
                const dataField = dataFields[key];
                const {
                    property,
                    format,
                    valueAliases,
                    globalValueAliases,
                } = dataField;

                acc[key] =
                    get(document, property) ||
                    get(document.meta, `${property}.value`, '');

                if (acc[key]) {
                    if (valueAliases || globalValueAliases) {
                        const aliases = this.getAliases(
                            valueAliases,
                            globalValueAliases
                        );
                        acc[key] = get(aliases, acc[key], acc[key]);
                    }

                    if (format) {
                        acc[key] = formatter({
                            format,
                            dataStore: this,
                            type: format.type,
                            locale: this.culture,
                            currency: '',
                            options: format.options,
                            value: acc[key],
                        });
                    }
                }

                return acc;
            }, {});
            return replacePlaceholders({
                values,
                text,
                appParams: this.appDataProps,
            });
        }
        return getDefaultText();
    }

    getDocumentDisplayName = (document: any, shareClass?: any) => {
        const { documentDisplayName: documentDisplayNameConfig } = this.config;
        let documentDisplayName = '';
        if (documentDisplayNameConfig && documentDisplayNameConfig.query) {
            const { entityType } = this.config;
            const queryOptions = JSON.parse(
                JSON.stringify(documentDisplayNameConfig.query)
            );
            const queryContext: IQueryContext = {
                response: document,
                entityByType: {
                    [entityType]: shareClass,
                },
            };

            documentDisplayName = this.queryStore.getValueFromQuery(
                queryOptions,
                queryContext
            );
        } else {
            documentDisplayName = this.getTextFromDocumentDataFields(
                document,
                documentDisplayNameConfig,
                () => {
                    return get(
                        this.config.cultureAliases,
                        document.cultureCode,
                        document.cultureCode
                    );
                }
            );
        }

        if (documentDisplayNameConfig && documentDisplayNameConfig.options) {
            const { options } = documentDisplayNameConfig;
            if (options.transforms) {
                documentDisplayName = this.applyTransforms(
                    documentDisplayName,
                    options.transforms,
                    shareClass,
                    null,
                    document
                );
            }
        }
        return documentDisplayName;
    };

    async getDocumentData(documents: Array<any>) {
        const docClientCodes = documents.map(document => document.clientCode);
        return this.isZipDocument(documents)
            ? this.services.documentDownloadZip(docClientCodes)
            : this.services.documentDownload(docClientCodes);
    }

    // Download documents zip with document clientCodes
    @action
    downloadDocuments = async (
        documents: Array<any> = [],
        shareClass?: any
    ) => {
        if (documents.length > 0) {
            const loadingId = this.stores.loading.start('global');
            try {
                const documentData = await this.getDocumentData(documents);
                FileSaver.saveAs(
                    documentData,
                    this.getFileName(documents, shareClass)
                );
            } catch (e) {
                console.warn(e);
            }

            this.stores.loading.stop('global', loadingId);
        }
    };

    getDocumentLink = (
        documents: Array<any>,
        shareClass?: any,
        urlQuery?: IQueryOptions
    ) => {
        if (documents && documents.length === 1) {
            const { clientCode } = documents[0];
            const { query } = this.stores;
            if (urlQuery) {
                const url = query.query(urlQuery, {
                    entityByType: {
                        [urlQuery.entityType]: {
                            ...(shareClass || {}),
                        },
                    },
                    document: documents[0],
                });
                return url;
            }
            const fileName = this.getFileName(documents, shareClass, false);
            const href = this.services.getRetrieveDocumentURL(
                clientCode,
                fileName
            );
            return href;
        }
        return null;
    };

    @action
    previewDocuments = async (
        documents: Array<any> = [],
        shareClass: any,
        options: PreviewActionOptions
    ) => {
        if (documents.length > 0) {
            const loadingId = this.stores.loading.start('global');
            try {
                if (options.url && !this.isZipDocument(documents)) {
                    const urlQuery = options.url;
                    const { query } = this.stores;
                    if (urlQuery) {
                        const url = query.query(urlQuery, {
                            entityByType: {
                                [urlQuery.entityType]: {
                                    ...(shareClass || {}),
                                },
                            },
                            document: documents[0],
                        });
                        this.openPreview(url);
                    }
                } else {
                    const documentData = await this.getDocumentData(documents);
                    let download = true;
                    if (!this.isZipDocument(documents)) {
                        const url = this.convertBlobToURL(documentData);
                        if (url) {
                            download = false;
                            this.openPreview(url);
                        }
                    }
                    if (download) {
                        FileSaver.saveAs(
                            documentData,
                            this.getFileName(documents, shareClass)
                        );
                    }
                }
            } catch (e) {
                console.warn(e);
            }

            this.stores.loading.stop('global', loadingId);
        }
    };

    openPreview = (url: string) => {
        const previewMode =
            this.config.documentPreviewMode === 'WINDOW'
                ? 'location=0,toolbar=0,titlebar=0,status=0,menubar=0,fullscreen=0'
                : undefined;
        window.open(url, '_blank', previewMode);
    };

    isZipDocument(documents: Array<any> = []) {
        return documents.length > 1;
    }

    convertBlobToURL(file: Blob): string | null {
        try {
            const urlCreator = window.webkitURL || window.URL;
            return urlCreator.createObjectURL(file);
        } catch (e) {
            console.error('Failed to convert image data to url source');
        }
        return null;
    }

    getExtensionFromUrl = (url: string | undefined): string => {
        let extension = '';
        if (url.indexOf('.') > -1) {
            extension = url
                .split(/[#?]/)[0]
                .split('.')
                .pop()
                .trim();
        }
        return extension;
    };

    getFileName = (
        documents: Array<any> = [],
        shareClass?: any,
        allowEmpty: boolean = true
    ) => {
        if (documents.length > 0) {
            const firstDocument = documents[0];
            if (firstDocument && firstDocument.clientCode !== undefined) {
                const { documentFilename } = this.config;

                const getDefaultTextFn = () => {
                    const { filename, clientCode } = firstDocument;
                    if (!isNullOrEmpty(filename)) {
                        return filename;
                    }
                    return clientCode;
                };

                let parsedFilename = '';
                if (documentFilename.query) {
                    const { entityType } = this.config;
                    const queryOptions = JSON.parse(
                        JSON.stringify(documentFilename.query)
                    );
                    const queryContext: IQueryContext = {
                        response: firstDocument,
                        entityByType: {
                            [entityType]: shareClass,
                        },
                    };

                    parsedFilename = this.queryStore.getValueFromQuery(
                        queryOptions,
                        queryContext
                    );
                } else {
                    parsedFilename = this.getTextFromDocumentDataFields(
                        firstDocument,
                        documentFilename,
                        getDefaultTextFn
                    );
                }

                const isZip = documents.length > 1 ? true : false;
                const fileName = isZip ? 'documents' : parsedFilename;

                if (!allowEmpty && isNullOrEmpty(fileName)) {
                    return undefined;
                }

                let fullFileName = fileName;

                const documentMimeType = isZip
                    ? 'application/zip'
                    : firstDocument.fileType;

                let extension = this.getFileNameExtension(
                    firstDocument.filename
                );

                if (
                    isZip ||
                    (!extension &&
                        firstDocument.fileType !== 'application/octet-stream')
                ) {
                    extension = getExtension(documentMimeType);
                }

                // For Linked documents the filename and the filetype are not available
                if (!isZip && isNullOrEmpty(extension) && firstDocument.URL) {
                    extension = this.getExtensionFromUrl(firstDocument.URL);
                }

                if (extension && !fullFileName.endsWith(extension)) {
                    fullFileName += `.${extension}`;
                }

                const { options: rawOptions } = documentFilename;
                let options = {
                    replacement: '_',
                };

                if (rawOptions) {
                    options = { replacement: rawOptions.replacement };
                    // Apply transforms on the value if configured
                    if (rawOptions.transforms) {
                        fullFileName = this.applyTransforms(
                            fullFileName,
                            rawOptions.transforms,
                            shareClass,
                            documents,
                            firstDocument
                        );
                    }
                }

                // Only pass through the replacement options
                return fileNameSanitizer(fullFileName, {
                    replacement: options.replacement,
                });
            }
        }
        return 'No Documents';
    };

    getFileNameExtension = (fileName: string): string => {
        // gets extension if it exists. Handles cases with no extension and files with '.' at the start of the name.
        // Updated to not include the "." this will align with the mime package getExtension
        const extension = fileName.slice(
            Math.max(0, fileName.lastIndexOf('.') + 1) || Infinity
        );
        return extension;
    };

    // Download CSV file from CSV string
    @action
    downloadCsv = async (csv: string, filename: string) => {
        const loadingId = this.stores.loading.start('global');

        try {
            const blob = new Blob([csv], {
                type: 'text/csv;charset=utf-8',
            });

            FileSaver.saveAs(blob, filename);
        } catch (e) {
            console.warn(e);
        }

        this.stores.loading.stop('global', loadingId);
    };

    getFundListsFromSegmentationFilter(
        segmentationFilter: SegmentationFilter
    ): string | Array<string> {
        let fundList = [];
        const segmentation = new Segmentation(segmentationFilter, this);
        if (segmentationFilter) {
            if (
                segmentationFilter.inclusionClauses &&
                segmentationFilter.inclusionClauses.length > 0
            ) {
                fundList.push(...segmentationFilter.inclusionClauses);
            }
        }

        if (
            !isEmpty(this.fundlist) &&
            (!segmentationFilter || !segmentationFilter.excludeBaseFundList)
        ) {
            fundList.push(this.fundlist);
        }
        fundList = segmentation.orchestrateList(fundList);
        // API doesn't work correctly passing an empty array, default to empty string
        return fundList.length > 0 ? fundList : '';
    }

    getFundListExclusionsFromSegmentationFilter(
        segmentationFilter: SegmentationFilter
    ): Array<string> {
        let fundListExclusion = [];
        const segmentation = new Segmentation(segmentationFilter, this);
        if (segmentationFilter) {
            if (
                segmentationFilter.exclusionClauses &&
                segmentationFilter.exclusionClauses.length > 0
            ) {
                fundListExclusion.push(...segmentationFilter.exclusionClauses);
            }
        }
        fundListExclusion = segmentation.orchestrateList(fundListExclusion);
        return fundListExclusion;
    }

    getEntityProperty(entity: IEntityEntity, property: string) {
        if (entity && entity.properties_pub) {
            const prop = entity.properties_pub[property];
            if (prop && prop.value) {
                return prop.value;
            }
            return prop;
        }
        return entity;
    }

    fundListContainsEntity(entity: IEntityEntity, key: string): boolean {
        const identifierProperty = this.config.identifierProperty;
        const shareClassId = this.getEntityProperty(entity, identifierProperty);
        return (
            this.fundDetailLists &&
            this.fundDetailLists[key] &&
            this.fundDetailLists[key].indexOf(shareClassId) >= 0
        );
    }

    @action
    toggleExpandStatus(entity: IEntity, expanded?: boolean) {
        const { type, clientCode } = entity;
        if (!this.expandStatus[type]) {
            this.expandStatus[type] = {};
        }
        if (expanded !== undefined) {
            this.expandStatus[type][clientCode] = expanded;
        }
        this.expandStatus[type][clientCode] = !this.getExpandStatus(entity);
        // trigger computed refresh
        this.expandStatus = { ...this.expandStatus };
    }

    getExpandStatus(entity: IEntity): boolean {
        const { type, clientCode } = entity;
        const expandStatusValue =
            this.expandStatus &&
            this.expandStatus[type] &&
            this.expandStatus[type][clientCode];

        return expandStatusValue !== undefined
            ? expandStatusValue
            : this.isExpanded;
    }

    applyTransforms(
        value: string,
        transforms: Array<ITransformOptions>,
        shareClass: any,
        documents,
        document
    ): string {
        const entityType = this.config.entityType;
        const queryContext: IQueryContext = {
            ...this.documentEntityData,
            entityByType: {
                [entityType]: shareClass,
                ...documents,
            },
            document,
        };
        const executionOptions = this.queryStore.getExecutionOptionsUsingContext(
            queryContext
        );
        return transformValue(
            value,
            transforms,
            this.stores.translation,
            executionOptions
        );
    }
}



// WEBPACK FOOTER //
// ./src/stores/DataStore.ts